import { Center, Plane, Resize, Svg, Text } from "@react-three/drei";
import * as React from "react";
import * as THREE from "three";
import { Hotspot, TourData } from "../../../../../Types";
import { useFrame } from "@react-three/fiber";
import { Interactive, useController, useXR } from "@react-three/xr";

import { useXRPopupStore } from "../XRHotspots";
import { useDataRootStore } from "../../../../../api/Zustand";
import XRHotspotLabel from "./XRHotspotLabel";
import { getLanguageFile } from "../../../../common/GetLanguageFile";
import { useCursorShrinkingStore } from "../../UI/CursorNew";
import { Box, Flex, useReflow } from "@react-three/flex";
import QuizButtonElement from "../../UI/QuizButtonElement";
import { textMeshMaterial } from "../Open/XRImagePop";

const center = new THREE.Vector3(0, 0, 0);

let timeout: NodeJS.Timeout;
let timeout2: NodeJS.Timeout;

function XRExternalLink(props: {
  pos: THREE.Vector3 | number[];
  hs: Hotspot;
  tourData: TourData;
}) {
  const xrPop = useXRPopupStore();
  const { pos, hs, tourData } = props;

  const iconRef = React.useRef<THREE.Mesh>(null);
  const svgRef = React.useRef<THREE.Object3D<Event>>(null);

  const [isHovering, setHovering] = React.useState<string | null>();

  const groupRef = React.useRef<THREE.Group>(null);

  const gazeController = useController("none");
  const leftController = useController("left");
  const rightController = useController("right");

  useFrame(() => {
    if (isHovering === hs.id) {
      iconRef.current?.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
    } else {
      iconRef.current?.scale.lerp(new THREE.Vector3(0.8, 0.8, 0.8), 0.1);
    }
  });

  const { root } = useDataRootStore();

  const cursor = useCursorShrinkingStore();

  const iconSrc = React.useMemo(() => {
    if (process.env.NODE_ENV === "development") {
      if (hs.type === "link") return "./svg/link.svg";
      else return "./svg/video.svg";
    } else {
      if (hs.type === "link") return root + "/svg/link.svg";
      else return root + "/svg/video.svg";
    }
  }, [hs.type, root]);

  if (root && !xrPop.isOpen)
    return (
      <group
        ref={groupRef}
        scale={1}
        onUpdate={(self) => self.lookAt(center)}
        position={pos as THREE.Vector3}
        visible={!xrPop.isOpen}
      >
        <Interactive
          key={hs.x + hs.y}
          onHover={() => {
            setHovering(hs.id);
            if (
              hs.type === "transition" &&
              tourData.scenes.find((one) => one.id === hs.target)
            ) {
              if (gazeController && !leftController && !rightController) {
                clearTimeout(timeout);
                clearTimeout(timeout2);

                timeout = setTimeout(() => {
                  cursor.setShrinking(true);
                  timeout2 = setTimeout(() => {
                    cursor.setShrinking(false);
                    xrPop.openSingularCustomPop(hs.id);
                  }, 2e3);
                }, 1e3);
              }
            }
          }}
          onBlur={() => {
            setHovering(undefined);
            cursor.setShrinking(false);
            clearTimeout(timeout);
            clearTimeout(timeout2);
          }}
          onSelect={() => {
            xrPop.openSingularCustomPop(hs.id);
          }}
        >
          <group scale={gazeController ? 1.6 : 0.6}>
            <mesh ref={iconRef} scale={0.8}>
              <mesh name="hotspot" renderOrder={6}>
                <planeGeometry args={[30, 30]}></planeGeometry>
                <meshBasicMaterial visible={false} />
                <Center>
                  <Resize
                    width={true}
                    height={true}
                    scale={hs.type === "link" ? 25 : 30}
                  >
                    <Svg
                      name="SVG"
                      ref={svgRef}
                      src={iconSrc}
                      fillMaterial={{
                        depthTest: false,
                        color: "white",
                      }}
                      strokeMaterial={{
                        color: "black",
                        depthTest: false,
                      }}
                      strokeMeshProps={{ position: [0, 0, 1] }}
                      renderOrder={2}
                    />
                  </Resize>
                </Center>
              </mesh>
            </mesh>

            <Center scale={30} visible={false}>
              <Resize>
                <Plane>
                  <meshBasicMaterial
                    depthTest={false}
                    transparent
                    wireframe
                  ></meshBasicMaterial>
                </Plane>
              </Resize>
            </Center>

            <XRHotspotLabel hoverState={isHovering === hs.id}>
              {hs.title || ""}
            </XRHotspotLabel>
          </group>
        </Interactive>
      </group>
    );

  if (!!xrPop.singularCustomIsOpen && xrPop.singularCustomIsOpen === hs.id)
    return (
      <Flex
        scale={gazeController ? 5 : 3}
        centerAnchor
        alignItems={"center"}
        size={[2, 5, 0]}
        onUpdate={(s) => s.lookAt(0, 0, 0)}
        position={pos as THREE.Vector3}
      >
        <Inner target={hs.target as string}></Inner>
      </Flex>
    );
}

const Inner = (props: { target: string }) => {
  const { session } = useXR();
  const xrPop = useXRPopupStore();
  const reflow = useReflow();

  const openLink = React.useCallback(
    (target: string) => {
      session?.end();
      xrPop.closePop();
      return window.open(target, "_href", "noreferrer");
    },
    [session, xrPop]
  );

  useFrame(reflow);

  return (
    <>
      <Box centerAnchor>
        <Text
          maxWidth={25}
          renderOrder={3}
          fontWeight={800}
          material={textMeshMaterial}
        >
          [messaggio temporaneo uscita da sessione vr e navigazione a sito]
        </Text>
      </Box>

      <Box
        marginTop={1}
        flexDir={"row"}
        justifyContent={"space-between"}
        width={20}
      >
        <Box centerAnchor>
          <QuizButtonElement
            fn={() => {
              xrPop.closePop();
            }}
          >
            {getLanguageFile("generics.cancel")}
          </QuizButtonElement>
        </Box>

        <Box centerAnchor>
          <QuizButtonElement fn={() => openLink(props.target)}>
            {getLanguageFile("generics.confirm")}
          </QuizButtonElement>
        </Box>
      </Box>
    </>
  );
};

export default XRExternalLink;
